import { DocumentViewer, DocumentWorkspace } from "@/components/ui/document-workspace";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { errorMessage } from "@/lib/api";
import { getInboxContent } from "@/lib/documentInbox";
import { getCompanyInboxContent } from "@/lib/companyInbox";
import { InboxThumbnail, loadInboxPages } from "./InboxPages";
import "./InboxDocumentPreview.css";

export function useInboxMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 899px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 899px)");
    const changed = () => setMobile(media.matches);
    media.addEventListener("change", changed);
    return () => media.removeEventListener("change", changed);
  }, []);
  return mobile;
}

export function InboxDocumentPreview({ id, mimeType, backfill = false, company = false, tabs, actions, title = "Pregled dokumenta", preloadedContent }: { id: string; mimeType?: string | null; backfill?: boolean; company?: boolean; title?: string; actions?: ReactNode | ((page: number) => ReactNode); tabs?: { id: string; label: string; content: ReactNode }[]; preloadedContent?: Blob }) {
  const mobile = useInboxMobile();
  const [content, setContent] = useState<{ id: string; url: string; mime: string; blob: Blob } | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    let url: string | undefined;
    (preloadedContent && !attempt ? Promise.resolve(preloadedContent) : company ? getCompanyInboxContent(id, controller.signal) : getInboxContent(id, controller.signal, backfill)).then((blob) => {
      if (controller.signal.aborted) return;
      url = URL.createObjectURL(blob);
      setContent({ id, url, blob, mime: blob.type || mimeType || "application/pdf" });
      setError(null);
    }).catch((cause: unknown) => {
      if (!controller.signal.aborted) setError({ id, message: errorMessage(cause, "Pregled se ne može učitati.") });
    });
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [id, mimeType, attempt, backfill, company, preloadedContent]);
  const current = content?.id === id ? content : null;
  const [pageSelection, setPageSelection] = useState({ id, page: 1 });
  const selectedPage = pageSelection.id === id ? pageSelection.page : 1;
  const [pageList, setPageList] = useState<{ id: string; pages: { page: number }[] } | null>(null);
  useEffect(() => {
    if (!current || current.mime !== "application/pdf" || backfill) return;
    const controller = new AbortController();
    loadInboxPages(id, controller.signal, company).then(pages => {
      if (!controller.signal.aborted && Array.isArray(pages)) setPageList({ id, pages });
    }).catch(() => { /* The original preview stays available if metadata fails. */ });
    return () => controller.abort();
  }, [id, current, backfill, company]);
  const pages = pageList?.id === id ? pageList.pages : [];
  const preview = current && pages.length > 1 ? <div className="inbox-page-preview">
    <nav className="inbox-page-strip" aria-label="Stranice dokumenta">{pages.map(({ page }) => <button type="button" key={page} aria-label={`Prikaži stranicu ${page}`} aria-current={selectedPage === page ? "page" : undefined} className="inbox-page-choice" onClick={() => setPageSelection({ id, page })}>
      <InboxThumbnail id={id} page={page} company={company} lazy className="inbox-page-thumbnail" /><span>{page}</span>
    </button>)}</nav>
    <div className="inbox-page-view"><InboxSelectedPage key={`${id}:${selectedPage}`} blob={current.blob} page={selectedPage} /></div>
  </div> : undefined;
  const fallback = error?.id === id ? <div role="alert">{error.message} <button className="underline" onClick={() => { setError(null); setAttempt(value => value + 1); }}>Ponovi pregled</button></div> : <p role="status">Učitavanje pregleda…</p>;
  const mobileLink = mobile && current ? <a href={current.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center underline">{current.mime.startsWith("image/") ? "Otvori dokument" : "Otvori PDF"}</a> : null;
  return <>{mobileLink && <div className="avm-inbox-document-link">{mobileLink}</div>}<DocumentWorkspace key={id} title={title} url={current?.url ?? null} mime={current?.mime} tabs={tabs} actions={typeof actions === "function" ? actions(pages.length > 1 ? selectedPage : 1) : actions} preview={preview} fallback={fallback} /></>;
}

function InboxSelectedPage({ blob, page }: { blob: Blob; page: number }) {
  const [url, setUrl] = useState(""); const [error, setError] = useState("");
  useEffect(() => {
    const controller = new AbortController(); let objectUrl = "";
    void import("@/components/site-planning/pdfDrawing").then(({ renderPdfPage }) => renderPdfPage(blob, page, controller.signal)).then(image => {
      if (!controller.signal.aborted) { objectUrl = URL.createObjectURL(image); setUrl(objectUrl); }
    }).catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, "Pregled stranice nije dostupan.")); });
    return () => { controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [blob, page]);
  return url ? <DocumentViewer url={url} mime="image/png" title={`Stranica ${page}`} /> : <p role={error ? "alert" : "status"}>{error || "Učitavanje stranice…"}</p>;
}
